import { Button } from '@studio/design-system/button';
import { Spinner } from '@studio/design-system/spinner';
import { StatusDeviceConnectionStatus } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { ReactElement } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Sel, STagNew, STagType } from '#~/stag/tag.js';
import { selectFailureReason } from '../telemetry.js';
import { onboardingContextFor, useOnboardingTracker } from '../hooks/use-onboarding-tracker.js';
import { useMultiDeviceStateQuery } from '#~/statequery/hook.js';
import { Viewer } from '#~/video/components/viewer/index.js';
import { FunnelShell } from '../components/funnel-shell.js';
import { CONTROL_CHECK_TIMEOUT_MS, STREAM_DECODE_TIMEOUT_MS } from '../constants.js';
import type { CheckStatus } from '../connection-checks.js';
import {
  isControlChecked,
  mayAdvance,
  resolveControlCheck,
  isAuthRefusal,
  resolveReachCheck,
  resolveStreamCheck
} from '../connection-checks.js';
import { useBridgeAddDevice } from '../hooks/use-bridge-add-device.js';
import { containerHasDecodedFrame } from '../video-readiness.js';
import type { Model } from '@studio/models/device';
import type { ControlConfig } from '../model-selection.js';
import type { CameraConfig, Device } from './types.js';
import { useStepView } from '../hooks/use-step-view.js';
import { leaveConnectionTest } from '../connection-test-navigation.js';
import type { OnboardingAddressSource } from '../telemetry.js';

// Connection test (ENG-3796 spec §5 Step 4 / prototype.html "Testing
// connection"). Three checks against the camera the user just picked.
//
// There is no bridge "probe" RPC, so the only way to test a camera is to ADD it
// and watch what the bridge reports. useBridgeAddDevice does that and marks the
// device provisional; commit() keeps it, and abandoning the screen removes it.
//
// Every check verdict is decided in connection-checks.ts, not here. That is
// where Didier's finding lives — a control row that ticked green for cameras
// with no control at all — and inside a component none of it could be unit
// tested. This file renders; it does not decide.
//
// Per rules: ai-code-requires-tests, code-comments-required,
// reference-rules-in-comments, ui-architecture (ai.knowledge/global).

interface ScreenConnectionTestProps {
  readonly camera: CameraConfig;
  /**
   * The control settings from the chosen model's registry template, aimed at
   * this camera. Undefined when the user did not identify the camera, and that
   * is what hides the control row — see connection-checks.ts isControlChecked.
   */
  readonly control?: ControlConfig | undefined;
  /** The chosen model, stamped on the device so the bridge resolves the template. */
  readonly modelId?: Model['id'] | undefined;
  /** Bridge that found this camera; it performs the add. */
  readonly agentId?: string | undefined;
  /** The discovery's own video configuration, passed to the add untouched. */
  readonly discoveredVideo?: unknown;
  /** Provenance for the resolved video object, which otherwise loses its origin. */
  readonly addressSource?: OnboardingAddressSource | undefined;
  /** ONVIF control endpoint to resolve the stream URI from, when there is no video config. */
  readonly onvifControl?: unknown;
  /** "PTZOptics Move 4K" style line under the checks, when known. */
  readonly detected?: string | undefined;
  /**
   * The device the Add Video Source workflow already committed. When present
   * this screen WATCHES it rather than adding a camera of its own.
   */
  readonly committedDeviceId?: Device['id'] | undefined;
  readonly onSuccess: (deviceId: Device['id']) => void;
  /** Step back to the camera-model screen while provisional-device cleanup continues. */
  readonly onBack: () => void;
  readonly onSkip: () => void;
  readonly onDismiss: () => void;
}

export const ScreenConnectionTest = ({
  camera,
  control,
  modelId,
  agentId,
  discoveredVideo,
  addressSource,
  onvifControl,
  detected,
  committedDeviceId,
  onSuccess,
  onBack,
  onSkip,
  onDismiss
}: ScreenConnectionTestProps): ReactElement => {
  useStepView('connection-test');

  // videoError is the bridge's own stream error for this device. It was produced
  // by the hook and never read here, which is why a 401 rendered as a timeout.
  const { result, add, adopt, reset, commit, controlStatus, videoError, markStreamObserved } =
    useBridgeAddDevice();
  const tracker = useOnboardingTracker();

  // A hidden Viewer, mounted once the bridge resolves the device, so this
  // screen can watch for a REAL decoded frame. Off-screen rather than
  // display:none: a display:none video does not decode in some engines, and
  // this container exists only for the readiness probe.
  const probeRef = useRef<HTMLDivElement>(null);
  const [videoReady, setVideoReady] = useState(false);
  const [streamTimedOut, setStreamTimedOut] = useState(false);
  const [controlTimedOut, setControlTimedOut] = useState(false);
  // A retry must be a NEW observation: it re-arms both timeouts and remounts
  // Viewer so LiveKit subscribes again.
  const [attempt, setAttempt] = useState(0);
  const committedRef = useRef(false);

  // The canonical control status. Per
  // `state-sync-status-field-chain-movement-connectionstatus` this field is
  // authoritative over movement.status, and connection-checks.ts treats its
  // silence as silence rather than as success.
  const canonicalMap = useMultiDeviceStateQuery<StatusDeviceConnectionStatus | undefined>({
    minInterval: 500,
    transform: (device) =>
      device.id === result.deviceId ? device.status?.movement?.connectionStatus?.status : undefined
  });
  const canonical = result.deviceId === undefined ? undefined : canonicalMap[result.deviceId];

  // NOTE: the camera's reported movement capability is deliberately NOT read
  // here. It hydrates after the device lands in depot, so using it made
  // `controlChecked` go true -> false -> true and the control row appeared,
  // vanished, and reappeared while the user watched (Aleksey, 2026-08-25).
  //
  // Visibility is decided by what we CONFIGURED, which is known at mount and
  // never changes: a control config was sent, so control is checked. If the
  // camera then turns out to have nothing to move, the check fails honestly
  // rather than the row disappearing mid-test.
  const controlChecked = isControlChecked({
    userWantsControl: control !== undefined,
    hasMovementCapability: undefined
  });

  // A camera that refuses a password ANSWERED. Passing this makes the reachable
  // row tick instead of crossing out, which is the honest reading and the whole
  // reason the signal exists.
  const authRefused = isAuthRefusal(videoError);

  const reachCheck = resolveReachCheck({
    attempt: result.status,
    authRefused,
    videoReady,
    canonical,
    streamTimedOut
  });
  const streamCheck = resolveStreamCheck({
    attempt: result.status,
    videoReady,
    timedOut: streamTimedOut
  });
  const controlCheck = resolveControlCheck({
    checked: controlChecked,
    attempt: result.status,
    canonical,
    movement: controlStatus,
    timedOut: controlTimedOut
  });

  // Add on mount and on each retry. The bridge has no probe RPC, so this IS
  // the test.
  useEffect(() => {
    tracker.trackStepStarted(onboardingContextFor('connection-test'), {
      attempt_number: attempt + 1
    });
    if (committedDeviceId !== undefined) {
      // The workflow's own commit already created it. Watch, do not add.
      adopt(committedDeviceId);

      return;
    }

    void add(camera, control, modelId, agentId, discoveredVideo, onvifControl, addressSource);
    // `add` is stable and `camera` is the screen's subject; re-running on
    // either would re-add the device mid-test.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  // Poll the hidden Viewer for a decoded frame. Polling rather than a `loadeddata`
  // listener because Viewer owns the <video> element and remounts it.
  //
  // Gated on a DEVICE ID, not on the add reporting 'connected'. Gating on the
  // latter deadlocked the whole screen: the add only reports connected once it
  // has seen the stream, and this poller was the thing that would have seen it,
  // so neither moved and every camera failed at the 15s ceiling — while the
  // studio behind the wizard decoded the same camera's video perfectly
  // (traced live 2026-08-25 against the Bolin at 192.168.0.73).
  useEffect(() => {
    if (result.deviceId === undefined || videoReady) return undefined;
    const id = setInterval(() => {
      const container = probeRef.current;
      if (container !== null && containerHasDecodedFrame(container)) {
        setVideoReady(true);
        // A frame is proof the camera streams; the add no longer needs to wait.
        markStreamObserved();
      }
    }, 250);
    return () => clearInterval(id);
  }, [result.deviceId, videoReady, attempt]);

  // Bounded waits. Both are re-armed by a retry via `attempt`.
  // Same correction: the clock starts when there is a device to watch.
  useEffect(() => {
    if (result.deviceId === undefined || videoReady) return undefined;
    const id = setTimeout(() => setStreamTimedOut(true), STREAM_DECODE_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [result.deviceId, videoReady, attempt]);

  useEffect(() => {
    if (!controlChecked || result.deviceId === undefined || controlCheck === 'done') {
      return undefined;
    }
    const id = setTimeout(() => setControlTimedOut(true), CONTROL_CHECK_TIMEOUT_MS);
    return () => clearTimeout(id);
  }, [controlChecked, result.deviceId, controlCheck, attempt]);

  // The four states a user can be STUCK in. Each reports once, with a reason
  // and a terminal flag — a raw error count cannot tell "still connecting" from
  // "gave up", and only one of those is a user who needs help.
  const reportedErrorRef = useRef<Set<string>>(new Set());
  const trackerRef = useRef(tracker);
  trackerRef.current = tracker;

  const reportFailure = useCallback(
    (failureAttempt: number, reason: string, rawError: string): void => {
      // The attempt belongs in the key. A Re-test that fails for the same reason
      // is a second completed attempt, while a re-render within one attempt is not.
      const failureKey = `${failureAttempt}:${reason}`;
      if (reportedErrorRef.current.has(failureKey)) return;
      reportedErrorRef.current.add(failureKey);

      // ENG-4305's `onboarding_failed`. It classifies the raw error itself, so the
      // bridge's own message is handed over verbatim rather than pre-bucketed
      // here; our local `reason` rides along as a property so the existing
      // breakdown still works.
      trackerRef.current.trackFailed({
        context: onboardingContextFor('connection-test'),
        rawError,
        // Terminal: the user cannot proceed from here without changing
        // something. Every one of these states blocks Save & continue.
        terminal: true,
        properties: { reason, attempt_number: failureAttempt + 1 }
      });
    },
    []
  );

  // A frame timeout does not end the attempt: the probe keeps polling, and a
  // frame that lands after the wait still passes this attempt. So that one
  // failure is held until the attempt ends while still frameless (Re-test,
  // Back, Skip, Escape or the screen closing) and dropped if a frame passes it;
  // a camera that then saves is never recorded as a dead end.
  const pendingNoVideoRef = useRef<
    { readonly attempt: number; readonly rawError: string } | undefined
  >(undefined);
  const flushPendingNoVideo = useCallback((): void => {
    const pending = pendingNoVideoRef.current;
    pendingNoVideoRef.current = undefined;
    if (pending !== undefined) reportFailure(pending.attempt, 'no_video', pending.rawError);
  }, [reportFailure]);
  useEffect(() => flushPendingNoVideo, [flushPendingNoVideo]);

  useEffect(() => {
    // Order asserted in telemetry.test.ts: a refusal outranks "no video",
    // because a refused camera has no video either and reporting that sends the
    // user to check an address that was already right.
    const reason = selectFailureReason({
      blocked: result.status === 'blocked',
      authRefused,
      streamFailed: streamCheck === 'error',
      controlChecked,
      controlFailed: controlCheck === 'error',
      addFailed: result.status === 'error'
    });

    if (reason === undefined) {
      // A late frame passed the attempt, so the held no-video failure did not happen.
      pendingNoVideoRef.current = undefined;
      return;
    }
    const rawError = result.error ?? reason;
    if (reason === 'no_video' && result.status === 'connected') {
      pendingNoVideoRef.current = { attempt, rawError };
      return;
    }
    pendingNoVideoRef.current = undefined;
    reportFailure(attempt, reason, rawError);
  }, [
    result.status,
    result.error,
    streamCheck,
    controlChecked,
    controlCheck,
    authRefused,
    attempt,
    reportFailure
  ]);

  const allPassed = mayAdvance({
    attempt: result.status,
    reachCheck,
    streamCheck,
    controlChecked,
    controlCheck
  });

  const keepAndAdvance = useCallback((): void => {
    const deviceId = result.deviceId;
    if (deviceId === undefined) return;
    // Advancing IS the user keeping the camera: commit marks the provisional
    // device kept so the abandon cleanup never removes it.
    if (!committedRef.current) {
      committedRef.current = true;
      commit();
    }
    tracker.trackStepSubmitted(onboardingContextFor('connection-test'), {
      attempt_number: attempt + 1,
      result: 'passed'
    });
    onSuccess(deviceId);
  }, [attempt, commit, onSuccess, result.deviceId, tracker]);

  // NO auto-advance. Passing the checks enables "Save & continue"; it does not
  // press it. A screen that moves on by itself takes the result away before the
  // user has read it, and this one is the only place they are shown what was
  // verified about their camera.

  // Leaving starts cleanup, but the Bridge save must not hold the user on this screen.
  const handleBack = useCallback((): void => {
    flushPendingNoVideo();
    leaveConnectionTest(reset, onBack);
  }, [flushPendingNoVideo, reset, onBack]);

  const handleSkip = useCallback((): void => {
    flushPendingNoVideo();
    onSkip();
  }, [flushPendingNoVideo, onSkip]);

  const handleDismiss = useCallback((): void => {
    flushPendingNoVideo();
    onDismiss();
  }, [flushPendingNoVideo, onDismiss]);

  const handleRetest = useCallback((): void => {
    flushPendingNoVideo();
    setVideoReady(false);
    setStreamTimedOut(false);
    setControlTimedOut(false);
    // Awaited: reset() removes the provisional device, and the plan source
    // limit is counted at the moment of the next add. Re-adding before the
    // removal lands is what made Re-test fail instantly against a camera the
    // bridge had never been asked about.
    void (async (): Promise<void> => {
      await reset();
      setAttempt((n) => n + 1);
    })();
  }, [flushPendingNoVideo, reset]);

  // A blocked attempt is NOT a failure to reach the camera — nothing was
  // contacted — so it keeps the neutral heading and lets the message above say
  // what actually happened.
  const blocked = result.status === 'blocked';
  const failed =
    !blocked &&
    (reachCheck === 'error' ||
      streamCheck === 'error' ||
      (controlChecked && controlCheck === 'error'));

  return (
    <FunnelShell
      announceTitle={failed ? "We couldn't reach that camera" : 'Testing connection'}
      onEscape={handleDismiss}
      progressStep={4}
    >
      {/* Hidden readiness probe. Mounted only once the bridge has a device. */}
      {result.deviceId !== undefined && (
        <div
          {...STagNew(Sel.OnboardingConnectionTest, 'video-probe', undefined, STagType.Data)}
          aria-hidden='true'
          className='pointer-events-none absolute h-px w-px overflow-hidden opacity-0'
          ref={probeRef}
        >
          <Viewer deviceId={result.deviceId} key={attempt} />
        </div>
      )}

      <h1 className='text-xl font-demibold text-white'>
        {blocked
          ? 'Cannot add this camera'
          : failed
            ? "We couldn't reach that camera"
            : allPassed
              ? 'Camera connected'
              : 'Testing connection…'}
      </h1>
      <p className='mt-12 max-w-[36rem] text-md text-foreground/75'>
        {controlChecked ? 'Three quick checks' : 'Two quick checks'} against{' '}
        <span className='font-mono text-foreground'>{camera.host}</span>.
      </p>

      <div
        aria-live='polite'
        className='mt-24 flex w-full max-w-[36rem] flex-col gap-8'
        role='status'
      >
        <CheckRow label='Reachable on network' status={reachCheck} />
        <CheckRow label='Video stream readable' status={streamCheck} />
        {/* Absent, not crossed, when nothing was tested: the user said the
            camera has no control, or the camera reports no movement. A row
            either way would be a claim about a check that never ran. */}
        {controlChecked && <CheckRow label='Camera control responds' status={controlCheck} />}

        {/* The add's own message. It was computed and never displayed, so a plan
            limit — which is not a camera fault at all — rendered as three
            crosses under "We couldn't reach that camera". */}
        {/* Credentials, said plainly, instead of the generic timeout copy. The
            bridge told us the camera refused us; repeating "check the IP and
            port" would send the user to look at the one thing that is right. */}
        {authRefused && (
          <div
            {...STagNew(Sel.OnboardingConnectionTest, 'auth_error', undefined, STagType.Data)}
            className='border-error/40 mt-8 rounded-12 border bg-error/10 px-16 py-12 text-left text-sm text-foreground'
            role='alert'
          >
            That camera refused the username and password. Go back and enter the ones it expects.
          </div>
        )}

        {/* What we actually observed, rather than one message for every
            failure. "Check that the IP and port are correct" is actively
            misleading for an NDI camera, which is addressed by source name
            (Aleksey, 2026-08-25). Each branch below names a state the traces
            distinguish. */}
        {result.error !== undefined && !authRefused && (
          <div
            {...STagNew(Sel.OnboardingConnectionTest, 'diagnosis', undefined, STagType.Data)}
            className='border-error/40 mt-8 rounded-12 border bg-error/10 px-16 py-12 text-left text-sm text-foreground'
            role='alert'
          >
            {videoError !== undefined
              ? `The Bridge reported: ${videoError}`
              : canonical === undefined
                ? 'The Bridge accepted this camera but never reported it connecting. That usually means the Bridge cannot reach it, or the camera refused the connection without answering.'
                : videoReady
                  ? 'The camera connected, but no video arrived.'
                  : 'The camera connected, but no video frame arrived before the time limit.'}
          </div>
        )}

        {detected !== undefined && (
          <div className='mt-8 rounded-12 border border-base-100 bg-base-100 px-16 py-12 text-left text-sm text-muted'>
            Detected: <span className='font-medium text-foreground'>{detected}</span>
          </div>
        )}
      </div>

      <div className='mt-24 flex w-full max-w-[36rem] items-center justify-between'>
        <div className='flex items-center gap-8'>
          {/* "Back", not "Edit details".
              A camera picked from a scan has no details to edit — the address
              came from the bridge, not from the user — so the prototype's label
              only makes sense on the manual-entry path, which is not built yet.
              Worse, going back left the provisional device in the room, so the
              rescan could not find the one camera the user was trying to
              re-pick: isDiscoveryInUse correctly hides a camera already added.
              handleBack starts removing it before navigation. */}
          <Button
            {...STagNew(Sel.OnboardingConnectionTest, 'back')}
            className='px-0 text-muted no-underline hover:text-foreground'
            onClick={handleBack}
            size='md'
            variant='link'
          >
            Back
          </Button>
          {/* Nothing left to re-test once every check has passed, and offering
              it invites the user to tear down a camera that just worked —
              handleRetest removes the device before adding it again. */}
          <Button
            {...STagNew(Sel.OnboardingConnectionTest, 'retest')}
            className='text-foreground'
            disabled={allPassed}
            onClick={handleRetest}
            size='md'
            variant='secondary'
          >
            Re-test
          </Button>
        </div>
        <div className='flex items-center gap-8'>
          {/* Hidden once the checks pass: the camera is already added and
              working, so "add later" describes nothing that can still happen,
              and leaving it next to Save & continue offers a choice between an
              action and a no-op. */}
          {!allPassed && (
            <Button
              {...STagNew(Sel.OnboardingConnectionTest, 'skip')}
              className='px-0 text-muted no-underline hover:text-foreground'
              onClick={handleSkip}
              size='md'
              variant='link'
            >
              Skip and add later
            </Button>
          )}
          <Button
            {...STagNew(Sel.OnboardingConnectionTest, 'save_continue')}
            disabled={!allPassed}
            onClick={keepAndAdvance}
            size='md'
            variant='primary'
          >
            Save &amp; continue
          </Button>
        </div>
      </div>
    </FunnelShell>
  );
};

// ── One check row ─────────────────────────────────────────────────────

const CheckRow = ({
  label,
  status
}: {
  readonly label: string;
  readonly status: CheckStatus;
}): ReactElement => (
  <div
    className='flex items-center gap-12 rounded-12 border border-base-100 bg-base-100 px-16 py-12'
    {...STagNew(Sel.OnboardingConnectionTest, `check_${label}`, undefined, STagType.Data)}
  >
    <span className='flex h-24 w-24 shrink-0 items-center justify-center'>
      <CheckGlyph status={status} />
    </span>
    <span className='text-left text-md text-foreground'>{label}</span>
  </div>
);

// Tick, cross, spinner, or an empty slot. `pending` deliberately draws nothing
// rather than a grey tick — a check that has not run has no verdict to show.
const CheckGlyph = ({ status }: { readonly status: CheckStatus }): ReactElement => {
  if (status === 'active') return <Spinner className='h-16 w-16 text-muted' />;

  if (status === 'pending') {
    return <span aria-hidden='true' className='h-8 w-8 rounded-full bg-base-200' />;
  }

  const done = status === 'done';

  return (
    <span
      className={`flex h-24 w-24 items-center justify-center rounded-full ${
        done ? 'bg-success/20 text-success' : 'bg-error/20 text-error'
      }`}
      role='img'
      aria-label={done ? 'passed' : 'failed'}
    >
      <svg aria-hidden='true' className='h-14 w-14' fill='none' viewBox='0 0 24 24'>
        <path
          d={done ? 'M5 13l4 4L19 7' : 'M6 6l12 12M18 6L6 18'}
          stroke='currentColor'
          strokeLinecap='round'
          strokeLinejoin='round'
          strokeWidth='2.5'
        />
      </svg>
    </span>
  );
};
